/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useState } from 'react';
import {
  Grid,
  Button,
  TextField,
  Card,
  CardActions,
  CardContent,
  Container,
  CircularProgress,
} from '@mui/material';
import { useMount, useUpdateEffect } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';

import {
  checkInfo,
  confirmCode,
  loadProfile,
  saveProfile,
  sendVerifyMsg,
} from '../../modules/profile';
import VerifiedField from './VerifiedField';

const errorCheckList = ['lastName', 'firstName', 'email', 'tel'];

const ProfileContainer = () => {
  const dispatch = useDispatch();
  const {
    user,
    pending,
    saved,
    checked,
    checking,
    error: e,
  } = useSelector((state) => state.profile);
  const [enabled, setEnabled] = useState(false);
  const [state, setState] = useState({
    lastName: '',
    firstName: '',
    email: null,
    tel: null,
    nickName: '',
    institution: '',
    verified: { email: false, tel: false },
  });
  const [error, setError] = useState({
    lastName: '',
    firstName: '',
    password: '',
    email: '',
    tel: '',
  });
  const {
    email: checkedEmail,
    tel: checkedTel,
    send: checkedSend,
    auth: checkedAuth,
  } = checked;

  const lastNameValidation = (value) => {
    if (!value) {
      setError((prev) => ({
        ...prev,
        lastName: '성과 이름을 입력하세요.',
        firstName: '',
      }));
      return;
    }
    if (!value) {
      setError((prev) => ({ ...prev, lastName: '성을 입력하세요.' }));
      return;
    }
    if (value && !value.match(/^[a-zA-Z가-힣]+$/)) {
      setError((prev) => ({
        ...prev,
        lastName: '성에 숫자, 공백 및 특수문자는 입력할 수 없습니다.',
      }));
      return;
    }
    setError((prev) => ({ ...prev, lastName: '' }));
  };

  const firstNameValidation = (value) => {
    if (!value) {
      setError((prev) => ({
        ...prev,
        lastName: '성과 이름을 입력하세요.',
        firstName: '',
      }));
      return;
    }
    if (value && !value.match(/^[a-zA-Z가-힣]+$/)) {
      setError((prev) => ({
        ...prev,
        firstName: '이름에 숫자, 공백 및 특수문자는 입력할 수 없습니다.',
      }));
      return;
    }
    if (!value) {
      setError((prev) => ({ ...prev, firstName: '이름을 입력하세요.' }));
      return;
    }

    setError((prev) => ({ ...prev, firstName: '' }));
  };

  const passwordValidation = (value) => {
    const checkPassword =
      /^(?=.*[A-Za-z])(?=.*[\d~!@#$%^&*_\-+=;:,.?])[A-Za-z\d~!@#$%^&*_\-+=;:,.?]{8,}$/;
    if (value === '') {
      setError((prev) => ({ ...prev, password: '비밀번호를 입력해주세요.' }));
      return;
    }
    if (!checkPassword.test(value)) {
      setError((prev) => ({
        ...prev,
        password:
          '비밀번호는 영문, 숫자 및 특수문자(~!@#$%^&*_-+=;:,.?)를 조합하여 8~20자 이하로 입력해야 합니다.',
      }));
      return;
    }
    if (state.password !== '' && value !== state.passwordConfirm) {
      setError((prev) => ({
        ...prev,
        password: '',
        passwordConfirm: '비밀번호가 일치하지 않습니다.',
      }));
      return;
    }
    setError((prev) => ({ ...prev, password: '', passwordConfirm: '' }));
  };

  const passwordConfirmValidation = (value) => {
    if (state.password !== value && value !== '') {
      setError((prev) => ({
        ...prev,
        passwordConfirm: '비밀번호가 일치하지 않습니다.',
      }));
      return;
    }
    if (
      state.password === value &&
      error.password !==
        '비밀번호는 영문, 숫자 및 특수문자(~!@#$%^&*_-+=;:,.?)를 조합하여 8~20자 이하로 입력해야 합니다.'
    ) {
      setError((prev) => ({ ...prev, passwordConfirm: '' }));
    }
  };

  const emailValidation = (value) => {
    const isCheckEmail =
      /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*.[a-zA-Z]{2,3}$/i;
    if (!value) {
      setError((prev) => ({ ...prev, email: '이메일을 입력해주세요.' }));
      return;
    }
    if (!isCheckEmail.test(value)) {
      setError((prev) => ({ ...prev, email: '유효한 메일 형식이 아닙니다.' }));
      return;
    }
    if (value.indexOf('@hanmail') > -1 || value.indexOf('@daum') > -1) {
      setError((prev) => ({
        ...prev,
        email:
          'daum 및 hanmail 이메일 계정으로는 인증메일을 수신할 수 없습니다. 다른 메일로 입력해주세요.',
      }));
      return;
    }
    setError((prev) => ({ ...prev, email: '' }));
  };
  const telValidation = (value) => {
    const isCheckTel = /\d{10,}/g;
    if (!value) {
      setError((prev) => ({ ...prev, tel: '이메일을 입력해주세요.' }));
      return;
    }
    if (!isCheckTel.test(value)) {
      setError((prev) => ({
        ...prev,
        tel: '유효한 전화번호 형식이 아닙니다.',
      }));
      return;
    }
    setError((prev) => ({ ...prev, tel: '' }));
  };

  const validation = (target) => {
    const { name, value } = target;
    switch (name) {
      case 'lastName':
        return lastNameValidation(value);
      case 'firstName':
        return firstNameValidation(value);
      case 'password':
        return passwordValidation(value);
      case 'passwordConfirm':
        return passwordConfirmValidation(value);
      case 'email':
        return emailValidation(value);
      case 'tel':
        return telValidation(value);
      default:
        return null;
    }
  };

  const onChange = (event) => {
    const { name, value } = event.target;
    setState({ ...state, [name]: value });
    validation({ name, value });
  };

  const onBlur = (event) => {
    const { name } = event.target;
    const index = errorCheckList.indexOf(name);
    const list = errorCheckList.slice(0, index + 1);
    list.map((check) => validation({ name: check, value: state[check] }));
  };

  const handleNext = () => {
    dispatch(saveProfile(state));
  };

  const onConfirm = (data) => {
    dispatch(confirmCode(data));
  };
  const onCheck = (data) => {
    dispatch(checkInfo(data));
  };
  const onSend = (data) => {
    dispatch(sendVerifyMsg({ ...data, userId: user.userId }));
  };

  useEffect(() => {
    const msg = Object.values(error).reduce((acc, cur) => !!cur || acc, false);
    if ('email' in state) {
      const emailVerified =
        (!state.verified.email && !state.email) ||
        (!!state.email && state.verified.email);
      const telVerified = !!state.tel && state.verified.tel;
      setEnabled(!msg && emailVerified && telVerified);
    }
  }, [error]);

  useEffect(() => {
    setError({ ...error, email: e.email, tel: e.tel });
  }, [e]);

  useEffect(() => {
    if (user) {
      setState({
        lastName: user.lastName,
        firstName: user.firstName,
        email: user.email,
        tel: user.tel,
        nickName: user.nickName || '',
        institution: user.institution || '',
        verified: user.verified || { tel: false, email: false },
      });
    }
  }, [user]);

  useUpdateEffect(() => saved && dispatch(loadProfile()), [saved]);
  useMount(() => dispatch(loadProfile()));

  return (
    <Container maxWidth="lg" sx={{ margin: 'auto' }}>
      <Card>
        <CardActions sx={{ pt: 3, pr: 3, justifyContent: 'end' }}>
          <Button
            color="secondary"
            variant="contained"
            disabled={!enabled || pending}
            onClick={handleNext}
          >
            완료
          </Button>
        </CardActions>
        <CardContent sx={{ minHeight: '540px' }}>
          {pending && (
            <Grid container spacing={2} maxWidth="sm" sx={{ margin: '0 auto' }}>
              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <CircularProgress color="secondary" />
              </Grid>
            </Grid>
          )}
          {!pending && (
            <Grid container spacing={2} maxWidth="sm" sx={{ margin: '0 auto' }}>
              <Grid item xs={12}>
                <TextField
                  required
                  name="userId"
                  label="아이디"
                  variant="outlined"
                  defaultValue={user.userId}
                  disabled
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="password"
                  label="비밀번호"
                  variant="outlined"
                  type="password"
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!error.password}
                  helperText={error.password}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="passwordConfirm"
                  label="비밀번호 확인"
                  variant="outlined"
                  type="password"
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!error.passwordConfirm}
                  helperText={error.passwordConfirm}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextField
                  required
                  name="lastName"
                  label="성"
                  variant="outlined"
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!error.lastName}
                  helperText={error.lastName}
                  defaultValue={user.lastName}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <TextField
                  required
                  name="firstName"
                  label="이름"
                  variant="outlined"
                  onChange={onChange}
                  onBlur={onBlur}
                  error={!!error.firstName}
                  helperText={error.firstName}
                  defaultValue={user.firstName}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  name="nickName"
                  label="닉네임"
                  variant="outlined"
                  onChange={onChange}
                  defaultValue={user.nickName}
                  fullWidth
                />
              </Grid>
              <VerifiedField
                {...{
                  checking,
                  checkedItem: checkedEmail,
                  checkedSend,
                  checkedAuth,
                  setError,
                  setState,
                  user,
                  state,
                  name: 'email',
                  label: '이메일',
                  defaultValue: user.email,
                  error,
                  onBlur,
                  onChange,
                  onCheck,
                  onConfirm,
                  onSend,
                }}
              />
              <VerifiedField
                {...{
                  checking,
                  checkedItem: checkedTel,
                  checkedSend,
                  checkedAuth,
                  setError,
                  setState,
                  user,
                  state,
                  name: 'tel',
                  label: '전화번호',
                  defaultValue: user.tel,
                  error,
                  onBlur,
                  onChange,
                  onCheck,
                  onConfirm,
                  onSend,
                }}
              />
              <Grid item xs={12}>
                <TextField
                  name="institution"
                  label="기관명"
                  variant="outlined"
                  onChange={onChange}
                  defaultValue={user.institution}
                  fullWidth
                />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};
export default ProfileContainer;
